import {
  CloseOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  defaultDropAnimationSideEffects,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Button,
  Col,
  Form,
  Image,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tooltip,
  Upload,
} from 'antd';
import { map } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppContext } from '../../../../../AppContext';
import {
  ALLOW_IMAGE_TYPES,
  MAX_IMAGE_SIZE,
  NUMBER_FIELD_TYPES,
  PRODUCT_TYPE,
  USER_ROLES,
} from '../../../../../common/constants';
import {
  fileUpload,
  formatNameToSlug,
  formValidatorRules,
  getBase64,
  getImageDimensions,
  openNotification,
  validateImage,
  validateVideo,
} from '../../../../../common/utils';
import CommonPreview from '../../../../../components/CommonPreview';
import NumberComponent from '../../../../../components/NumberComponent';
import SelectComponent from '../../../../../components/SelectComponent';
import AgeGroupSelect from './AgeGroupSelect';
import BrandSelect from './BrandSelect';
import CategorySelect from './CategorySelect';
import ContentMasterSelect from './ContentMasterSelect';
import FacilitatesSelect from './FacilitatesSelect';
import GenresSelect from './GenresSelect';
import MaterialSelect from './MaterialSelect';
import PackagingMaterialSelect from './PackagingMaterialSelect';
import TagsSelect from './TagsSelect';
import ToyChecklist from './ToyChecklist';
import ToyRequiredContents from './ToyRequiredContents';
import SortableImageItem, { ImageDropZone } from './SortableImageItem';
import { GET_TOY_CONTENT_IMAGES_SIGNED_PUT_URL } from '../graphql/Queries';

const { Item } = Form;
const { TextArea } = Input;
const { url, price, positiveNumber, required } = formValidatorRules;
const MAX_TOY_IMAGES = 9;

const ToyForm = ({
  form,
  onFinish,
  handleShowPrompt,
  initialFormValues,
  isView,
  toyImages,
  setToyImages,
  toyVideo,
  setToyVideo,
  toyDetail,
  featuredImage,
  setFeaturedImage,
  isEdit,
  onSubmittingChange,
}) => {
  const {
    state: { currentUser },
  } = useContext(AppContext);
  const productName = Form.useWatch('name', form);
  const toyNumber = Form.useWatch('id', form);
  const watchedRequiredContents = Form.useWatch('requiredContents', form);
  const [visible, setVisible] = useState(false);
  const [requiredContentImagesVisible, setRequiredContentImagesVisible] =
    useState(false);
  const [requiredContentPreviewImages, setRequiredContentPreviewImages] =
    useState([]);
  const [requiredContentPreviewFieldIndex, setRequiredContentPreviewFieldIndex] =
    useState(null);
  const [contentImageLoading, setContentImageLoading] = useState({});
  const activeImageUploads = useRef(0);
  const pendingContentImageUploads = useRef({});
  const previewObjectUrls = useRef(new Set());
  const [toyPreviewImage, setToyPreviewImage] = useState();
  const [toyPreviewVideo, setToyPreviewVideo] = useState();
  const [inCludedContentValues, setInCludedContentValues] = useState([]);

  const [getToyContentImagesSignedPutUrl] = useLazyQuery(
    GET_TOY_CONTENT_IMAGES_SIGNED_PUT_URL,
    {
      fetchPolicy: 'network-only',
    },
  );

  const canEditRequiredContentImages =
    !isView &&
    !toyDetail?.isArchived &&
    currentUser?.roles?.includes(USER_ROLES?.SUPER_ADMIN?.key);

  const createPreviewObjectUrl = (file) => {
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrls.current.add(objectUrl);
    return objectUrl;
  };

  const revokePreviewObjectUrl = (objectUrl) => {
    if (previewObjectUrls.current.has(objectUrl)) {
      URL.revokeObjectURL(objectUrl);
      previewObjectUrls.current.delete(objectUrl);
    }
  };

  useEffect(
    () => () => {
      previewObjectUrls.current.forEach((objectUrl) =>
        URL.revokeObjectURL(objectUrl),
      );
      previewObjectUrls.current.clear();
    },
    [],
  );

  useEffect(() => {
    const activeObjectUrls = new Set([
      ...(watchedRequiredContents || []).flatMap((content) =>
        content?.imageUrls || [],
      ),
      toyPreviewVideo,
    ]);
    previewObjectUrls.current.forEach((objectUrl) => {
      if (!activeObjectUrls.has(objectUrl)) {
        revokePreviewObjectUrl(objectUrl);
      }
    });
  }, [toyPreviewVideo, watchedRequiredContents]);

  const handleRequiredContentImageUpload = async (
    fieldIndex,
    files,
    fieldKey,
  ) => {
    const contents = form?.getFieldValue('requiredContents') || [];
    const row = contents[fieldIndex];
    if (!row || !files?.length) return;

    const uploadRowId = row?.contentUploadRowId || `content-row-${fieldKey}`;
    form?.setFieldValue(
      ['requiredContents', fieldIndex, 'contentUploadRowId'],
      uploadRowId,
    );
    const pendingCount = pendingContentImageUploads.current[uploadRowId] || 0;
    const imageKeys = row?.imageKeys || [];
    const imageUrls = row?.imageUrls || [];
    const imageCount = Math.max(imageKeys.length, imageUrls.length);
    const remainingCount = Math.max(0, 4 - imageCount - pendingCount);

    if (files?.length > remainingCount) {
      openNotification('error', 'You can upload up to 4 images per content.');
      return;
    }
    if (!toyNumber) {
      openNotification('error', 'Enter Product ID before uploading images.');
      return;
    }

    const invalidFile = files?.find((file) => {
      const extension = file?.name?.split('.')?.pop()?.toLowerCase();
      return (
        !ALLOW_IMAGE_TYPES?.includes(extension) || file?.size > MAX_IMAGE_SIZE
      );
    });
    if (invalidFile) {
      openNotification(
        'error',
        'Use JPEG, JPG, PNG, or GIF images up to 15 MB.',
      );
      return;
    }

    activeImageUploads.current += 1;
    pendingContentImageUploads.current[uploadRowId] = pendingCount + files.length;
    onSubmittingChange?.(true);
    setContentImageLoading((current) => ({ ...current, [fieldKey]: true }));
    try {
      const fileNames = files?.map((file, index) => {
        const extension = file?.name?.split('.')?.pop();
        return `content-master-image-${Date.now()}-${fieldKey}-${index}.${extension}`;
      });
      const response = await getToyContentImagesSignedPutUrl({
        variables: {
          data: { toyNumber, fileNames },
        },
      });
      const signedUrls =
        response?.data?.getToyContentImagesSignedPutUrl || [];
      if (signedUrls?.length !== files?.length) {
        throw new Error('Could not prepare all image uploads. Please try again.');
      }
      const uploadedKeys = await Promise.all(
        signedUrls?.map(async (signedData, index) => {
          await fileUpload(signedData?.signedUrl, files[index]);
          return signedData?.fileName;
        }),
      );
      const latestContents = form?.getFieldValue('requiredContents') || [];
      const latestFieldIndex = latestContents.findIndex(
        (content) => content?.contentUploadRowId === uploadRowId,
      );
      if (latestFieldIndex === -1) {
        openNotification(
          'warning',
          'The required content row was removed before upload finished. Its images were not attached.',
        );
        return;
      }
      const latestImageKeys =
        latestContents[latestFieldIndex]?.imageKeys || [];
      const latestImageUrls =
        latestContents[latestFieldIndex]?.imageUrls || [];
      if (Math.max(latestImageKeys.length, latestImageUrls.length) + uploadedKeys.length > 4) {
        throw new Error('You can upload up to 4 images per content.');
      }
      const uploadedObjectUrls = files.map((file) =>
        createPreviewObjectUrl(file),
      );
      form?.setFieldValue(
        ['requiredContents', latestFieldIndex, 'imageKeys'],
        [...latestImageKeys, ...uploadedKeys],
      );
      form?.setFieldValue(
        ['requiredContents', latestFieldIndex, 'imageUrls'],
        [...latestImageUrls, ...uploadedObjectUrls],
      );
      handleShowPrompt();
    } catch (error) {
      openNotification('error', error?.message || 'Image upload failed.');
    } finally {
      pendingContentImageUploads.current[uploadRowId] = Math.max(
        0,
        (pendingContentImageUploads.current[uploadRowId] || 0) - files.length,
      );
      setContentImageLoading((current) => ({
        ...current,
        [fieldKey]: pendingContentImageUploads.current[uploadRowId] > 0,
      }));
      activeImageUploads.current -= 1;
      if (activeImageUploads.current === 0) onSubmittingChange?.(false);
    }
  };

  const handleRequiredContentImagePreview = (imageUrls, fieldIndex) => {
    const images = (imageUrls || [])
      .map((image) => (typeof image === 'string' ? image : image?.url))
      .filter(Boolean)
      .map((imageUrl) => ({ key: imageUrl, url: imageUrl }));
    if (images.length) {
      setRequiredContentPreviewImages(images);
      setRequiredContentPreviewFieldIndex(fieldIndex);
      setRequiredContentImagesVisible(true);
    }
  };

  const handleRemoveRequiredContentImage = (imageIndex) => {
    const fieldIndex = requiredContentPreviewFieldIndex;
    const imageKeys =
      form?.getFieldValue([
        'requiredContents',
        fieldIndex,
        'imageKeys',
      ]) || [];
    const imageUrls =
      form?.getFieldValue([
        'requiredContents',
        fieldIndex,
        'imageUrls',
      ]) || [];

    form?.setFieldValue(
      ['requiredContents', fieldIndex, 'imageKeys'],
      imageKeys.filter((_, index) => index !== imageIndex),
    );
    form?.setFieldValue(
      ['requiredContents', fieldIndex, 'imageUrls'],
      imageUrls.filter((_, index) => index !== imageIndex),
    );
    revokePreviewObjectUrl(imageUrls[imageIndex]);
    setRequiredContentPreviewImages((current) =>
      current.filter((_, index) => index !== imageIndex),
    );
    handleShowPrompt();
  };

  const [validationTriggered, setValidationTriggered] = useState(false);

  useEffect(() => {
    setInCludedContentValues(
      toyDetail?.includedContents?.map((contentMaster) => contentMaster?.id) ||
        [],
    );
    const initialValueFun = () => {
      form?.setFieldsValue(initialFormValues);
    };
    setTimeout(initialValueFun, 300);
  }, []);

  useEffect(() => {
    if (
      productName &&
      (!initialFormValues?.slug || initialFormValues?.name !== productName)
    ) {
      form.setFieldsValue({
        slug: formatNameToSlug(productName),
      });
    }
  }, [productName, form]);

  const onFinishFailed = () => {
    setValidationTriggered(true);
  };

  // Validates each selected file so multiple selection rejects only the invalid ones
  const handleImagesBeforeUpload = async (file, fileList) => {
    const remainingSlots = MAX_TOY_IMAGES - (toyImages?.length || 0);
    const fileIndex = fileList?.indexOf(file);
    if (fileIndex >= remainingSlots) {
      if (fileIndex === Math.max(remainingSlots, 0)) {
        message?.error(
          `You can upload up to ${MAX_TOY_IMAGES} images. ${
            fileList.length - Math.max(remainingSlots, 0)
          } image(s) were not added.`,
        );
      }
      return Upload.LIST_IGNORE;
    }

    const ext = file?.name?.substring(file?.name?.lastIndexOf('.') + 1);
    if (!ALLOW_IMAGE_TYPES?.includes(ext?.toLowerCase())) {
      message?.error(`${file?.name} file is not image file.`);
      return Upload.LIST_IGNORE;
    }
    if (file?.size > MAX_IMAGE_SIZE) {
      message?.error(`${file?.name}: Allowed maximum size is 15Mb`);
      return Upload.LIST_IGNORE;
    }
    try {
      const { width, height } = (await getImageDimensions(file)) || {};
      if (width <= 200 || height <= 200) {
        message?.error(
          `${file?.name}: The image is too small, the minimum dimensions are 200x200 pixels`,
        );
        return Upload.LIST_IGNORE;
      }
    } catch (error) {
      message?.error(`${file?.name} file is not image file.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleChange = ({ fileList }) => {
    setToyImages(fileList);
    form.setFieldsValue({ images: fileList });
  };

  const handleVideoChange = async (info) => {
    const res = await validateVideo(info, form, setToyVideo, 'video', true);
    if (res) {
      revokePreviewObjectUrl(toyPreviewVideo);
      setToyPreviewVideo(createPreviewObjectUrl(info?.fileList?.[0]?.originFileObj));
    }
  };

  const handleFeaturedImageChange = (info) => {
    validateImage(info, form, setFeaturedImage, 'featuredImages');
  };

  const isImageDragDisabled = !!isView || !!toyDetail?.isArchived;
  const [activeDragImage, setActiveDragImage] = useState(null);
  const imageDropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: { active: { opacity: '0.4' } },
    }),
  };
  const imageDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const getSortableImageId = (list, file) => `${list}:${file?.uid}`;

  const renderSortableImage = (list) => (originNode, file) => {
    const { uid } = file || {};
    const index = (list === 'images' ? toyImages : featuredImage)?.findIndex(
      (image) => image?.uid === uid,
    );
    return (
      <SortableImageItem
        id={getSortableImageId(list, file)}
        list={list}
        index={index}
        disabled={isImageDragDisabled}
      >
        {originNode}
      </SortableImageItem>
    );
  };

  const handleImageDragStart = ({ active }) => {
    const { list, index } = active?.data?.current || {};
    setActiveDragImage(
      (list === 'images' ? toyImages : featuredImage)?.[index] || null,
    );
  };

  const handleImageDragEnd = ({ active, over }) => {
    setActiveDragImage(null);
    const from = active?.data?.current;
    const to = over?.data?.current;
    if (!from || !to || active?.id === over?.id) return;

    const nextFeaturedImage = [...(featuredImage || [])];
    let nextToyImages = [...(toyImages || [])];

    if (from.list === 'images' && to.list === 'images') {
      // Reorder within images
      nextToyImages = arrayMove(nextToyImages, from.index, to.index);
    } else if (from.list === 'images' && to.list === 'featuredImages') {
      // Promote image to featured, previous featured takes its place
      const [movedImage] = nextToyImages.splice(
        from.index,
        1,
        ...nextFeaturedImage.slice(0, 1),
      );
      nextFeaturedImage.splice(0, 1, movedImage);
    } else if (from.list === 'featuredImages' && to.list === 'images') {
      // Swap featured with the image it was dropped on
      const [swappedImage] = nextToyImages.splice(
        to.index,
        1,
        nextFeaturedImage[0],
      );
      nextFeaturedImage.splice(0, 1, swappedImage);
    } else {
      return;
    }

    setFeaturedImage(nextFeaturedImage);
    setToyImages(nextToyImages);
    form.setFieldsValue({
      featuredImages: nextFeaturedImage,
      images: nextToyImages,
    });
    handleShowPrompt();
  };

  const renderImageLabel = (label, info) => (
    <Space size={6}>
      {label}
      {!isImageDragDisabled && (
        <Tooltip title={info} trigger={['hover', 'focus']}>
          <InfoCircleOutlined
            className="pointer"
            tabIndex={0}
            aria-label={`${label} information`}
          />
        </Tooltip>
      )}
    </Space>
  );

  const handlePreview = async (fileObj) => {
    let preview;
    if (!fileObj?.url && !fileObj?.preview) {
      preview = await getBase64(fileObj?.originFileObj);
    }
    setVisible(true);
    setToyPreviewImage(fileObj?.url || preview);
  };

  const handleIncludedContent = (value, fieldIndex) => {
    const newSelectedValues = [...inCludedContentValues];
    newSelectedValues[fieldIndex] = value;
    setInCludedContentValues(newSelectedValues);
  };

  return (
    <>
      <CommonPreview
        visible={visible}
        setVisible={setVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />
      <Modal
        title="Required Content Images"
        open={requiredContentImagesVisible}
        onCancel={() => setRequiredContentImagesVisible(false)}
        footer={null}
        width={720}
      >
        <Space wrap size="middle" className="toy-content-image-preview-list">
          {requiredContentPreviewImages.map((image, index) => (
            <div className="toy-content-image-preview-item" key={image.key}>
              <Image
                width={140}
                height={140}
                src={image.url}
                alt={`Required content ${index + 1}`}
                style={{ objectFit: 'cover' }}
              />
              {!isView && canEditRequiredContentImages && (
                <Popconfirm
                  title="Remove this image?"
                  okText="Remove"
                  cancelText="Cancel"
                  onConfirm={() => handleRemoveRequiredContentImage(index)}
                >
                  <Button
                    danger
                    size="small"
                    className="toy-content-image-preview-remove"
                    aria-label={`Remove image ${index + 1}`}
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              )}
            </div>
          ))}
        </Space>
      </Modal>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        onFieldsChange={handleShowPrompt}
        disabled={!!isView || toyDetail?.isArchived}
        onFinishFailed={onFinishFailed}
        scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
        className={isView ? 'common-view-form' : 'form'}
        validateTrigger={validationTriggered ? 'onChange' : 'onSubmit'}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={8}>
            <Item
              label="Product ID"
              name="id"
              rules={[{ ...required, message: 'Please Enter Product ID!' }]}
            >
              <Input
                allowClear
                placeholder="Enter Product ID"
                maxLength={50}
                disabled={isEdit}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={8}>
            <Item
              label="Product Name"
              name="name"
              rules={[{ ...required, message: 'Please Enter Product Name!' }]}
            >
              <Input
                allowClear
                placeholder="Enter Product Name"
                maxLength={200}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Item
              label="Product Slug"
              name="slug"
              rules={[{ ...required, message: 'Please Enter Product Slug!' }]}
            >
              <Input
                allowClear
                placeholder="Enter Product Slug"
                maxLength={200}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={8}>
            <Item
              label="Product Price"
              name="price"
              rules={[
                {
                  required: true,
                  message: 'Please Enter Product Price!',
                },
                price,
              ]}
            >
              <NumberComponent
                allowClear
                placeholder="Enter Product Price"
                type={NUMBER_FIELD_TYPES?.PRICE}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Item label="EAN Number" name="eanNumber" rules={[positiveNumber]}>
              <NumberComponent allowClear placeholder="Enter EAN Number" />
            </Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Item label="Product Dimensions" name="dimensions">
              <Input
                allowClear
                placeholder="Enter Product Dimensions"
                maxLength={200}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Item label="Product URL" name="url" rules={[url]}>
              <Input allowClear placeholder="Enter Product URL" />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8} xl={8} xxl={8}>
            <Item
              label="Total Carbon Emission (kg)"
              name="carbonEmission"
              rules={[
                {
                  required: true,
                  message: 'Please Enter Product Carbon Emission!',
                },
                price,
              ]}
            >
              <NumberComponent
                allowClear
                placeholder="Enter Product Carbon Emission"
                type={NUMBER_FIELD_TYPES?.CARBON_EMISSION}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Item
              label="Type"
              name="type"
              initialValue={PRODUCT_TYPE?.TOY?.value}
            >
              <SelectComponent
                placeholder="Select Type"
                options={map(PRODUCT_TYPE, (res) => res)}
              />
            </Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <Item
              label="Short Description"
              name="shortDescription"
              rules={[
                {
                  ...required,
                  message: 'Please Enter Product Short Description!',
                },
              ]}
            >
              <TextArea
                rows={2}
                allowClear
                placeholder="Enter Product Description"
                showCount
                maxLength={400}
              />
            </Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <Item
              label="Description"
              name="description"
              rules={[
                {
                  ...required,
                  message: 'Please Enter Product Description!',
                },
              ]}
            >
              <TextArea
                rows={4}
                allowClear
                placeholder="Enter Product Description"
                showCount
                maxLength={1200}
              />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item
              label="Categories"
              name="category"
              rules={[
                {
                  required: true,
                  message: 'Please Select Product Categories!',
                },
              ]}
            >
              <CategorySelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item
              label="Age Groups"
              name="ageGroup"
              rules={[{ required: true, message: 'Please Select Age Groups!' }]}
            >
              <AgeGroupSelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item
              label="Brand"
              name="brand"
              rules={[{ required: true, message: 'Please Select Brand!' }]}
            >
              <BrandSelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item
              label="Material"
              name="material"
              rules={[{ required: true, message: 'Please Select Material!' }]}
            >
              <MaterialSelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item label="Facilitates" name="facilitates">
              <FacilitatesSelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item label="Tags" name="tags">
              <TagsSelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item label="Genres" name="genres">
              <GenresSelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Item label="Packaging Material" name="packagingMaterial">
              <PackagingMaterialSelect toyDetail={toyDetail} />
            </Item>
          </Col>
          <Col xs={24} sm={12} className="content-master-form-list">
            <Item label="Included Content Master">
              <Form.List name="includedContentMasters">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Space
                        key={key}
                        align="baseline"
                        className="content-master-wrapper"
                      >
                        <Space.Compact>
                          <Form.Item
                            {...restField}
                            name={[name, 'id']}
                            rules={[
                              {
                                ...required,
                                message: 'Please Select Content Master!',
                              },
                            ]}
                          >
                            <ContentMasterSelect
                              form={form}
                              toyDetail={toyDetail}
                              index={index}
                              contentMasters={toyDetail?.includedContents}
                              inCludedContentValues={inCludedContentValues}
                              onChange={(value) =>
                                handleIncludedContent(value, index)
                              }
                              includedContentsSelect
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'quantity']}
                            rules={[
                              {
                                required: true,
                                message: 'Please Enter Quantity!',
                              },
                            ]}
                            getValueFromEvent={(event) => {
                              const { value } = event.target;
                              const parsedValue = parseInt(value, 10);
                              return Number.isNaN(parsedValue)
                                ? 0
                                : parsedValue;
                            }}
                          >
                            <NumberComponent
                              allowClear
                              isFractional={false}
                              placeholder="Enter Quantity"
                              type={NUMBER_FIELD_TYPES?.QUANTITY}
                            />
                          </Form.Item>
                        </Space.Compact>
                        <Button type="link" disabled={isView}>
                          <CloseOutlined onClick={() => remove(name)} />
                        </Button>
                      </Space>
                    ))}
                    {fields.length < 20 && (
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Content Master
                        </Button>
                      </Form.Item>
                    )}
                  </>
                )}
              </Form.List>
            </Item>
          </Col>
          <Col xs={24}>
            <ToyRequiredContents
              isView={isView}
              form={form}
              canEditImages={canEditRequiredContentImages}
              onUploadImages={handleRequiredContentImageUpload}
              onPreviewImages={handleRequiredContentImagePreview}
              contentImageLoading={contentImageLoading}
            />
          </Col>
          <Col xs={24}>
            <ToyChecklist isView={isView} />
          </Col>
          <DndContext
            sensors={imageDragSensors}
            collisionDetection={pointerWithin}
            onDragStart={handleImageDragStart}
            onDragEnd={handleImageDragEnd}
            onDragCancel={() => setActiveDragImage(null)}
          >
            <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
              <SortableContext
                items={(featuredImage || []).map((file) =>
                  getSortableImageId('featuredImages', file),
                )}
                strategy={rectSortingStrategy}
              >
                <Item
                  label={renderImageLabel(
                    'Featured Image',
                    'Drag an image from Images and drop it here to make it the featured image. The current featured image will take its place.',
                  )}
                  name="featuredImages"
                  rules={[
                    {
                      required: true,
                      message: 'Please Upload Featured Image!',
                    },
                  ]}
                >
                  <Upload
                    name="avatar"
                    listType="picture-card"
                    maxCount={1}
                    fileList={featuredImage}
                    beforeUpload={() => false}
                    onPreview={handlePreview}
                    onChange={handleFeaturedImageChange}
                    className="avatar-uploader"
                    itemRender={renderSortableImage('featuredImages')}
                    showUploadList={{
                      showDownloadIcon: false,
                      showPreviewIcon: true,
                      showRemoveIcon: true,
                    }}
                  >
                    {!isView && featuredImage?.length !== 1 && (
                      <ImageDropZone
                        id="featuredImages"
                        disabled={isImageDragDisabled}
                      >
                        <PlusOutlined />
                      </ImageDropZone>
                    )}
                  </Upload>
                </Item>
              </SortableContext>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
              <SortableContext
                items={(toyImages || []).map((file) =>
                  getSortableImageId('images', file),
                )}
                strategy={rectSortingStrategy}
              >
                <Item
                  label={renderImageLabel(
                    'Images',
                    'Drag and drop images to change their order. Drop an image on the featured image to swap them.',
                  )}
                  name="images"
                  rules={[{ required: true, message: 'Please Upload Images!' }]}
                >
                  <Upload
                    name="avatar"
                    listType="picture-card"
                    multiple
                    maxCount={MAX_TOY_IMAGES}
                    fileList={toyImages}
                    beforeUpload={handleImagesBeforeUpload}
                    onPreview={handlePreview}
                    onChange={handleChange}
                    className="avatar-uploader"
                    itemRender={renderSortableImage('images')}
                    showUploadList={{
                      showDownloadIcon: false,
                      showPreviewIcon: true,
                      showRemoveIcon: true,
                    }}
                  >
                    {!isView && toyImages?.length < MAX_TOY_IMAGES && (
                      <PlusOutlined />
                    )}
                  </Upload>
                </Item>
              </SortableContext>
            </Col>
            {createPortal(
              <DragOverlay dropAnimation={imageDropAnimation} zIndex={2000}>
                {activeDragImage ? (
                  <div className="toy-image-drag-overlay">
                    <img
                      src={
                        activeDragImage?.url ||
                        activeDragImage?.thumbUrl ||
                        activeDragImage?.preview
                      }
                      alt={activeDragImage?.name || 'Dragged image'}
                    />
                  </div>
                ) : null}
              </DragOverlay>,
              // eslint-disable-next-line no-undef
              document.body,
            )}
          </DndContext>
          <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
            <Item label="Video" name="video">
              {!toyPreviewVideo && !toyVideo?.url ? (
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  fileList={toyVideo}
                  beforeUpload={() => false}
                  onChange={handleVideoChange}
                  showUploadList={{
                    showDownloadIcon: false,
                    showPreviewIcon: false,
                    showRemoveIcon: false,
                  }}
                >
                  {toyVideo?.length !== 1 && <PlusOutlined />}
                </Upload>
              ) : (
                <>
                  <div className="toy-video-container">
                    <video
                      width="100%"
                      height="100%"
                      controls
                      src={toyPreviewVideo || toyVideo?.url}
                      className="toy-video"
                    >
                      <track kind="captions" />
                    </video>
                    {!isView && (
                      <DeleteOutlined
                        className="toy-video-delete-icon"
                        onClick={() => {
                          setToyVideo([]);
                          revokePreviewObjectUrl(toyPreviewVideo);
                          setToyPreviewVideo();
                          handleShowPrompt();
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default ToyForm;
